import React, { useEffect, useMemo, useState } from "react";
import API_BASE_URL from "../../config";
import "./BookMeSettingsPanel.css";

function getToken() {
  return localStorage.getItem("token");
}

function getBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
}

function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function normalizeDateOnly(value) {
  if (!value) return "";

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return formatDate(value);
  }

  const raw = String(value).trim();

  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return raw.slice(0, 10);
  }

  const parsed = new Date(raw);

  if (!Number.isNaN(parsed.getTime())) {
    return formatDate(parsed);
  }

  return "";
}

function formatDisplayDate(dateString) {
  const normalizedDate = normalizeDateOnly(dateString);

  if (!normalizedDate) return "";

  const [year, month, day] = normalizedDate.split("-").map(Number);
  const date = new Date(year, month - 1, day);

  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "long",
    day: "numeric",
    year: "numeric"
  });
}

function buildAppointmentRange() {
  const start = new Date();
  const end = new Date();

  end.setDate(start.getDate() + 30);

  return {
    startDate: formatDate(start),
    endDate: formatDate(end)
  };
}

function buildUnavailableDateRange() {
  return buildAppointmentRange();
}

const DEFAULT_PERMISSION_DISCLAIMER =
  "By booking this meeting, I agree that the agent may contact me by phone, text, or email about my appointment and related insurance services.";

const TIMEZONE_OPTIONS = [
  { value: "America/New_York", label: "Eastern Time — America/New_York" },
  { value: "America/Chicago", label: "Central Time — America/Chicago" },
  { value: "America/Denver", label: "Mountain Time — America/Denver" },
  { value: "America/Phoenix", label: "Arizona Time — America/Phoenix" },
  { value: "America/Los_Angeles", label: "Pacific Time — America/Los_Angeles" },
  { value: "America/Anchorage", label: "Alaska Time — America/Anchorage" },
  { value: "Pacific/Honolulu", label: "Hawaii Time — Pacific/Honolulu" },
  { value: "America/Puerto_Rico", label: "Puerto Rico — America/Puerto_Rico" }
];

const DAYS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" }
];

function createDefaultDayAvailability(dayOfWeek, enabled = false) {
  return {
    dayOfWeek,
    enabled,
    blocks: [
      {
        startTime: "09:00",
        endTime: "17:00",
        enabled: true
      },
      {
        startTime: "14:00",
        endTime: "17:00",
        enabled: false
      }
    ]
  };
}

const DEFAULT_AVAILABILITY = [
  createDefaultDayAvailability(1, true),
  createDefaultDayAvailability(2, true),
  createDefaultDayAvailability(3, true),
  createDefaultDayAvailability(4, true),
  createDefaultDayAvailability(5, true),
  createDefaultDayAvailability(0, false),
  createDefaultDayAvailability(6, false)
];

const EMPTY_MEETING_TYPE = {
  name: "",
  description: "",
  permissionDisclaimer: DEFAULT_PERMISSION_DISCLAIMER,
  durationMinutes: 30,
  bufferBeforeMinutes: 0,
  bufferAfterMinutes: 15,
  locationType: "phone",
  locationValue: "Phone call",
  sortOrder: 1,
  isActive: true
};

function normalizeAvailability(rows = []) {
  return DAYS.map((day) => {
    const dayRows = rows
      .filter((row) => Number(row.day_of_week) === Number(day.value))
      .sort((a, b) => String(a.start_time || "").localeCompare(String(b.start_time || "")))
      .slice(0, 2);

    const defaultDay = createDefaultDayAvailability(day.value, dayRows.length > 0);

    if (dayRows[0]) {
      defaultDay.blocks[0] = {
        startTime: dayRows[0].start_time?.slice(0, 5) || "09:00",
        endTime: dayRows[0].end_time?.slice(0, 5) || "17:00",
        enabled: true
      };
    }

    if (dayRows[1]) {
      defaultDay.blocks[1] = {
        startTime: dayRows[1].start_time?.slice(0, 5) || "14:00",
        endTime: dayRows[1].end_time?.slice(0, 5) || "17:00",
        enabled: true
      };
    }

    return defaultDay;
  });
}

function normalizeUnavailableDates(rows = []) {
  return rows
    .map((row) => ({
      unavailableDate: normalizeDateOnly(
        row.unavailable_date || row.unavailableDate || row.date
      ),
      reason: row.reason || ""
    }))
    .filter((row) => row.unavailableDate)
    .sort((a, b) => a.unavailableDate.localeCompare(b.unavailableDate));
}

function buildMeetingTypeForm(meetingType) {
  if (!meetingType) return EMPTY_MEETING_TYPE;

  return {
    name: meetingType.name || "",
    description: meetingType.description || "",
    permissionDisclaimer:
      meetingType.permission_disclaimer ||
      meetingType.permissionDisclaimer ||
      DEFAULT_PERMISSION_DISCLAIMER,
    durationMinutes: Number(meetingType.duration_minutes || meetingType.durationMinutes || 30),
    bufferBeforeMinutes: Number(
      meetingType.buffer_before_minutes || meetingType.bufferBeforeMinutes || 0
    ),
    bufferAfterMinutes: Number(
      meetingType.buffer_after_minutes || meetingType.bufferAfterMinutes || 15
    ),
    locationType: meetingType.location_type || meetingType.locationType || "phone",
    locationValue: meetingType.location_value || meetingType.locationValue || "",
    sortOrder: Number(meetingType.sort_order || meetingType.sortOrder || 1),
    isActive:
      meetingType.is_active === true ||
      meetingType.is_active === 1 ||
      meetingType.isActive === true
  };
}

function buildMeetingTypePayload(form) {
  return {
    name: form.name,
    description: form.description,
    permissionDisclaimer: form.permissionDisclaimer,
    durationMinutes: Number(form.durationMinutes || 30),
    bufferBeforeMinutes: Number(form.bufferBeforeMinutes || 0),
    bufferAfterMinutes: Number(form.bufferAfterMinutes || 0),
    locationType: form.locationType,
    locationValue: form.locationValue,
    sortOrder: Number(form.sortOrder || 0),
    isActive: Boolean(form.isActive)
  };
}

function Field({ label, children, full = false }) {
  return (
    <div className={`bookme-field ${full ? "bookme-field-full" : ""}`}>
      {label ? <label>{label}</label> : null}
      {children}
    </div>
  );
}

const BookMeSettingsPanel = () => {
  const browserTimezone = getBrowserTimezone();
  const unavailableDateRange = useMemo(() => buildUnavailableDateRange(), []);

  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [savingUnavailableDates, setSavingUnavailableDates] = useState(false);
  const [savingMeetingType, setSavingMeetingType] = useState(false);
  const [savingEditedMeetingType, setSavingEditedMeetingType] = useState(false);

  const [profile, setProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({
    slug: "",
    displayName: "",
    headline: "",
    description: "",
    timezone: browserTimezone || "America/Chicago",
    isActive: true
  });

  const [meetingTypes, setMeetingTypes] = useState([]);
  const [meetingTypeForm, setMeetingTypeForm] = useState(EMPTY_MEETING_TYPE);
  const [showCreateMeetingTypeForm, setShowCreateMeetingTypeForm] = useState(false);
  const [editingMeetingTypeId, setEditingMeetingTypeId] = useState(null);
  const [editingMeetingTypeForm, setEditingMeetingTypeForm] = useState(EMPTY_MEETING_TYPE);

  const [availability, setAvailability] = useState(DEFAULT_AVAILABILITY);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [newUnavailableDate, setNewUnavailableDate] = useState("");
  const [newUnavailableReason, setNewUnavailableReason] = useState("");
  const [appointments, setAppointments] = useState([]);

  const timezoneOptions = useMemo(() => {
    if (
      profileForm.timezone &&
      !TIMEZONE_OPTIONS.some((item) => item.value === profileForm.timezone)
    ) {
      return [
        {
          value: profileForm.timezone,
          label: `${profileForm.timezone} — Current saved timezone`
        },
        ...TIMEZONE_OPTIONS
      ];
    }

    return TIMEZONE_OPTIONS;
  }, [profileForm.timezone]);

  const publicBookingUrl = useMemo(() => {
    if (!profileForm.slug) return "";
    return `${window.location.origin}/bookme/${profileForm.slug}`;
  }, [profileForm.slug]);

  const iframeEmbedCode = useMemo(() => {
    if (!publicBookingUrl) return "";

    return `<iframe
  src="${publicBookingUrl}?embed=true"
  width="100%"
  height="800"
  style="border:0;border-radius:12px;"
></iframe>`;
  }, [publicBookingUrl]);

  const api = async (path, options = {}) => {
    const token = getToken();

    if (!token) {
      throw new Error("You must be logged in.");
    }

    const res = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...(options.headers || {})
      }
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(data.error || data.message || "Request failed.");
    }

    return data;
  };

  const loadBookMe = async () => {
    try {
      setLoading(true);

      const { startDate, endDate } = buildAppointmentRange();
      const unavailableRange = buildUnavailableDateRange();

      const [
        profileResult,
        meetingTypesResult,
        availabilityResult,
        unavailableDatesResult,
        appointmentsResult
      ] = await Promise.all([
        api("/api/bookme/profile"),
        api("/api/bookme/meeting-types"),
        api("/api/bookme/availability"),
        api(
          `/api/bookme/unavailable-dates?startDate=${unavailableRange.startDate}&endDate=${unavailableRange.endDate}`
        ),
        api(`/api/bookme/appointments?startDate=${startDate}&endDate=${endDate}`)
      ]);

      const loadedProfile = profileResult.profile;
      const nextTimezone =
        loadedProfile?.timezone ||
        browserTimezone ||
        "America/Chicago";

      setProfile(loadedProfile);
      setProfileForm({
        slug: loadedProfile?.slug || "",
        displayName: loadedProfile?.display_name || "",
        headline: loadedProfile?.headline || "",
        description: loadedProfile?.description || "",
        timezone: nextTimezone,
        isActive: Boolean(loadedProfile?.is_active)
      });

      setMeetingTypes(meetingTypesResult.meetingTypes || []);
      setAvailability(normalizeAvailability(availabilityResult.availability || []));
      setUnavailableDates(
        normalizeUnavailableDates(unavailableDatesResult.unavailableDates || [])
      );
      setAppointments(appointmentsResult.appointments || []);
    } catch (err) {
      console.error("Load BookMe settings error:", err);
      alert(err.message || "Failed to load BookMe settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBookMe();
  }, []);

  const handleCopy = async (value, label) => {
    if (!value) {
      alert(`${label} is not available yet.`);
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      alert(`${label} copied.`);
    } catch {
      alert("Copy failed. Please highlight and copy manually.");
    }
  };

  const handleUseBrowserTimezone = () => {
    const detectedTimezone = getBrowserTimezone();

    if (!detectedTimezone) {
      alert("Could not detect your browser timezone.");
      return;
    }

    setProfileForm((prev) => ({
      ...prev,
      timezone: detectedTimezone
    }));
  };

  const updateAvailabilityDay = (index, updates) => {
    setAvailability((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        ...updates
      };
      return next;
    });
  };

  const updateAvailabilityBlock = (dayIndex, blockIndex, updates) => {
    setAvailability((prev) => {
      const next = [...prev];
      const day = next[dayIndex];

      next[dayIndex] = {
        ...day,
        blocks: day.blocks.map((block, index) =>
          index === blockIndex
            ? {
                ...block,
                ...updates
              }
            : block
        )
      };

      return next;
    });
  };

  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);

      const result = await api("/api/bookme/profile", {
        method: "PUT",
        body: JSON.stringify({
          slug: profileForm.slug,
          displayName: profileForm.displayName,
          headline: profileForm.headline,
          description: profileForm.description,
          timezone: profileForm.timezone,
          isActive: profileForm.isActive
        })
      });

      setProfile(result.profile);
      alert("BookMe profile saved.");
    } catch (err) {
      console.error("Save BookMe profile error:", err);
      alert(err.message || "Failed to save BookMe profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleCreateMeetingType = async () => {
    try {
      setSavingMeetingType(true);

      const result = await api("/api/bookme/meeting-types", {
        method: "POST",
        body: JSON.stringify(buildMeetingTypePayload(meetingTypeForm))
      });

      setMeetingTypes((prev) => [...prev, result.meetingType]);
      setMeetingTypeForm(EMPTY_MEETING_TYPE);
      setShowCreateMeetingTypeForm(false);
      alert("Meeting type created.");
    } catch (err) {
      console.error("Create meeting type error:", err);
      alert(err.message || "Failed to create meeting type.");
    } finally {
      setSavingMeetingType(false);
    }
  };

  const handleStartEditMeetingType = (meetingType) => {
    setEditingMeetingTypeId(meetingType.id);
    setEditingMeetingTypeForm(buildMeetingTypeForm(meetingType));
  };

  const handleCancelEditMeetingType = () => {
    setEditingMeetingTypeId(null);
    setEditingMeetingTypeForm(EMPTY_MEETING_TYPE);
  };

  const handleSaveEditedMeetingType = async () => {
    if (!editingMeetingTypeId) return;

    try {
      setSavingEditedMeetingType(true);

      const result = await api(`/api/bookme/meeting-types/${editingMeetingTypeId}`, {
        method: "PUT",
        body: JSON.stringify(buildMeetingTypePayload(editingMeetingTypeForm))
      });

      setMeetingTypes((prev) =>
        prev.map((item) =>
          Number(item.id) === Number(editingMeetingTypeId)
            ? result.meetingType
            : item
        )
      );

      handleCancelEditMeetingType();
      alert("Meeting type updated.");
    } catch (err) {
      console.error("Update meeting type error:", err);
      alert(err.message || "Failed to update meeting type.");
    } finally {
      setSavingEditedMeetingType(false);
    }
  };

  const handleDeactivateMeetingType = async (meetingType) => {
    if (!window.confirm(`Deactivate "${meetingType.name}"?`)) return;

    try {
      const result = await api(`/api/bookme/meeting-types/${meetingType.id}`, {
        method: "PUT",
        body: JSON.stringify({
          ...buildMeetingTypePayload(buildMeetingTypeForm(meetingType)),
          isActive: false
        })
      });

      setMeetingTypes((prev) =>
        prev.map((item) =>
          Number(item.id) === Number(meetingType.id)
            ? result.meetingType
            : item
        )
      );

      alert("Meeting type deactivated.");
    } catch (err) {
      console.error("Deactivate meeting type error:", err);
      alert(err.message || "Failed to deactivate meeting type.");
    }
  };

  const handleSaveAvailability = async () => {
    try {
      setSavingAvailability(true);

      const rows = availability.flatMap((day) => {
        if (!day.enabled) return [];

        return day.blocks
          .filter((block, blockIndex) => blockIndex === 0 || block.enabled)
          .map((block) => ({
            dayOfWeek: day.dayOfWeek,
            startTime: block.startTime,
            endTime: block.endTime
          }));
      });

      const result = await api("/api/bookme/availability", {
        method: "PUT",
        body: JSON.stringify({ availability: rows })
      });

      setAvailability(normalizeAvailability(result.availability || []));
      alert("Availability saved.");
    } catch (err) {
      console.error("Save availability error:", err);
      alert(err.message || "Failed to save availability.");
    } finally {
      setSavingAvailability(false);
    }
  };

  const handleRemoveUnavailableDate = (dateToRemove) => {
    setUnavailableDates((prev) =>
      prev.filter((item) => item.unavailableDate !== dateToRemove)
    );
  };

  const handleSaveUnavailableDates = async () => {
    try {
      setSavingUnavailableDates(true);

      const pendingDate = normalizeDateOnly(newUnavailableDate);
      let datesToSave = [...unavailableDates];

      if (pendingDate) {
        if (
          pendingDate < unavailableDateRange.startDate ||
          pendingDate > unavailableDateRange.endDate
        ) {
          alert(
            `Unavailable date must be between ${unavailableDateRange.startDate} and ${unavailableDateRange.endDate}.`
          );
          return;
        }

        const alreadyExists = datesToSave.some(
          (item) => item.unavailableDate === pendingDate
        );

        if (!alreadyExists) {
          datesToSave.push({
            unavailableDate: pendingDate,
            reason: newUnavailableReason.trim()
          });
        }
      }

      datesToSave = datesToSave
        .filter((item) => item.unavailableDate)
        .sort((a, b) => a.unavailableDate.localeCompare(b.unavailableDate));

      const result = await api("/api/bookme/unavailable-dates", {
        method: "PUT",
        body: JSON.stringify({
          unavailableDates: datesToSave.map((item) => ({
            unavailableDate: item.unavailableDate,
            reason: item.reason || ""
          }))
        })
      });

      setUnavailableDates(
        normalizeUnavailableDates(result.unavailableDates || [])
      );

      setNewUnavailableDate("");
      setNewUnavailableReason("");

      alert("Unavailable dates saved.");
    } catch (err) {
      console.error("Save unavailable dates error:", err);
      alert(err.message || "Failed to save unavailable dates.");
    } finally {
      setSavingUnavailableDates(false);
    }
  };

  const handleCancelAppointment = async (appointmentId) => {
    if (!window.confirm("Cancel this BookMe appointment?")) return;

    try {
      await api(`/api/bookme/appointments/${appointmentId}/cancel`, {
        method: "PUT"
      });

      await loadBookMe();
      alert("Appointment cancelled.");
    } catch (err) {
      console.error("Cancel appointment error:", err);
      alert(err.message || "Failed to cancel appointment.");
    }
  };

  const renderMeetingTypeForm = (form, setForm, mode = "create") => (
    <div className="bookme-form-grid bookme-create-meeting-grid">
      <Field label="Name">
        <input
          value={form.name}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, name: e.target.value }))
          }
          placeholder="Consultation"
        />
      </Field>

      <Field label="Duration">
        <select
          value={form.durationMinutes}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              durationMinutes: Number(e.target.value)
            }))
          }
        >
          <option value={15}>15 minutes</option>
          <option value={30}>30 minutes</option>
          <option value={45}>45 minutes</option>
          <option value={60}>60 minutes</option>
          <option value={90}>90 minutes</option>
          <option value={120}>120 minutes</option>
        </select>
      </Field>

      <Field label="Buffer After">
        <select
          value={form.bufferAfterMinutes}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              bufferAfterMinutes: Number(e.target.value)
            }))
          }
        >
          <option value={0}>No buffer</option>
          <option value={5}>5 minutes</option>
          <option value={10}>10 minutes</option>
          <option value={15}>15 minutes</option>
          <option value={30}>30 minutes</option>
        </select>
      </Field>

      <Field label="Location Type">
        <select
          value={form.locationType}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              locationType: e.target.value
            }))
          }
        >
          <option value="phone">Phone</option>
          <option value="custom_link">Custom Link</option>
          <option value="in_person">In Person</option>
          <option value="google_meet">Google Meet</option>
          <option value="microsoft_teams">Microsoft Teams</option>
        </select>
      </Field>

      <Field label="Location Value" full>
        <input
          value={form.locationValue}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              locationValue: e.target.value
            }))
          }
          placeholder="Phone call, office address, or meeting link"
        />
      </Field>

      <Field label="Description" full>
        <textarea
          value={form.description}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              description: e.target.value
            }))
          }
          rows={3}
          placeholder="Describe what this meeting is for."
        />
      </Field>

      <Field label="Permission to Contact Disclaimer" full>
        <textarea
          value={form.permissionDisclaimer}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              permissionDisclaimer: e.target.value
            }))
          }
          rows={4}
          placeholder={DEFAULT_PERMISSION_DISCLAIMER}
        />
      </Field>

      {mode === "edit" ? (
        <Field label="">
          <label className="bookme-checkbox-label">
            <input
              type="checkbox"
              checked={Boolean(form.isActive)}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  isActive: e.target.checked
                }))
              }
            />
            Meeting type active
          </label>
        </Field>
      ) : null}
    </div>
  );

  if (loading) {
    return (
      <div className="sales-section-card sales-section-lead">
        <div className="sales-section-head">
          <h2>BookMe</h2>
          <p>Loading BookMe settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bookme-settings-stack">
      <div className="sales-section-card sales-section-lead">
        <div className="sales-section-head">
          <h2>BookMe Profile</h2>
          <p>Manage your public booking page, timezone, and booking link.</p>
        </div>

        <div className="bookme-form-grid">
          <Field label="Display Name">
            <input
              value={profileForm.displayName}
              onChange={(e) =>
                setProfileForm((prev) => ({ ...prev, displayName: e.target.value }))
              }
            />
          </Field>

          <Field label="Booking Slug">
            <input
              value={profileForm.slug}
              onChange={(e) =>
                setProfileForm((prev) => ({ ...prev, slug: e.target.value }))
              }
            />
          </Field>

          <Field label="Headline" full>
            <input
              value={profileForm.headline}
              onChange={(e) =>
                setProfileForm((prev) => ({ ...prev, headline: e.target.value }))
              }
            />
          </Field>

          <Field label="Description" full>
            <textarea
              value={profileForm.description}
              onChange={(e) =>
                setProfileForm((prev) => ({ ...prev, description: e.target.value }))
              }
              rows={3}
            />
          </Field>

          <Field label="Calendar / Booking Timezone">
            <div className="bookme-inline-actions">
              <select
                value={profileForm.timezone}
                onChange={(e) =>
                  setProfileForm((prev) => ({ ...prev, timezone: e.target.value }))
                }
              >
                {timezoneOptions.map((timezone) => (
                  <option key={timezone.value} value={timezone.value}>
                    {timezone.label}
                  </option>
                ))}
              </select>

              <button
                type="button"
                className="sales-secondary-btn"
                onClick={handleUseBrowserTimezone}
              >
                Use Mine
              </button>
            </div>
          </Field>

          <Field label="">
            <label className="bookme-checkbox-label">
              <input
                type="checkbox"
                checked={profileForm.isActive}
                onChange={(e) =>
                  setProfileForm((prev) => ({ ...prev, isActive: e.target.checked }))
                }
              />
              Booking page active
            </label>
          </Field>

          <Field label="Public Booking Link" full>
            <div className="bookme-inline-actions">
              <input value={publicBookingUrl} readOnly />
              <button
                type="button"
                className="sales-secondary-btn"
                onClick={() => handleCopy(publicBookingUrl, "BookMe link")}
              >
                Copy
              </button>
            </div>
          </Field>

          <Field label="Website Embed Code" full>
            <div className="bookme-inline-actions">
              <textarea value={iframeEmbedCode} readOnly rows={5} />
              <button
                type="button"
                className="sales-secondary-btn"
                onClick={() => handleCopy(iframeEmbedCode, "BookMe embed code")}
              >
                Copy
              </button>
            </div>
          </Field>
        </div>

        <div className="bookme-actions">
          <button
            className="sales-primary-btn"
            onClick={handleSaveProfile}
            disabled={savingProfile}
          >
            {savingProfile ? "Saving..." : "Save BookMe Profile"}
          </button>
        </div>
      </div>

      <div className="sales-section-card sales-section-subscription">
        <div className="sales-section-head">
          <h2>Meeting Types</h2>
          <p>Create, edit, activate, or deactivate the appointment types visitors can book.</p>
        </div>

        <div className="bookme-actions" style={{ marginTop: 0, marginBottom: "12px" }}>
          <button
            type="button"
            className="sales-primary-btn"
            onClick={() => setShowCreateMeetingTypeForm((prev) => !prev)}
          >
            {showCreateMeetingTypeForm ? "Hide New Meeting Type" : "+ New Meeting Type"}
          </button>
        </div>

        {showCreateMeetingTypeForm ? (
          <div className="bookme-create-meeting-panel">
            <div className="sales-section-head">
              <h3>Create Meeting Type</h3>
              <p>Add a new meeting type visitors can book from your public page.</p>
            </div>

            {renderMeetingTypeForm(meetingTypeForm, setMeetingTypeForm, "create")}

            <div className="bookme-actions">
              <button
                className="sales-primary-btn"
                onClick={handleCreateMeetingType}
                disabled={savingMeetingType}
              >
                {savingMeetingType ? "Creating..." : "Create Meeting Type"}
              </button>

              <button
                type="button"
                className="sales-secondary-btn"
                onClick={() => {
                  setMeetingTypeForm(EMPTY_MEETING_TYPE);
                  setShowCreateMeetingTypeForm(false);
                }}
                disabled={savingMeetingType}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : null}

        <div className="bookme-meeting-list">
          {meetingTypes.length === 0 ? (
            <p className="settings-muted-text">No meeting types yet.</p>
          ) : (
            meetingTypes.map((meetingType) => (
              <div key={meetingType.id} className="bookme-meeting-card">
                {editingMeetingTypeId === meetingType.id ? (
                  <div style={{ width: "100%" }}>
                    <div className="sales-section-head">
                      <h3>Edit Meeting Type</h3>
                      <p>Update the description, disclaimer, timing, and status.</p>
                    </div>

                    {renderMeetingTypeForm(
                      editingMeetingTypeForm,
                      setEditingMeetingTypeForm,
                      "edit"
                    )}

                    <div className="bookme-actions">
                      <button
                        type="button"
                        className="sales-primary-btn"
                        onClick={handleSaveEditedMeetingType}
                        disabled={savingEditedMeetingType}
                      >
                        {savingEditedMeetingType ? "Saving..." : "Save Changes"}
                      </button>

                      <button
                        type="button"
                        className="sales-secondary-btn"
                        onClick={handleCancelEditMeetingType}
                        disabled={savingEditedMeetingType}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <strong>{meetingType.name}</strong>
                      <p>
                        {meetingType.duration_minutes} minutes · {meetingType.location_type}
                      </p>
                      {meetingType.description ? (
                        <p className="settings-muted-text">{meetingType.description}</p>
                      ) : null}
                    </div>

                    <div className="bookme-inline-actions">
                      <span>{meetingType.is_active ? "Active" : "Inactive"}</span>

                      <button
                        type="button"
                        className="sales-secondary-btn"
                        onClick={() => handleStartEditMeetingType(meetingType)}
                      >
                        Edit
                      </button>

                      {meetingType.is_active ? (
                        <button
                          type="button"
                          className="sales-secondary-btn"
                          onClick={() => handleDeactivateMeetingType(meetingType)}
                        >
                          Deactivate
                        </button>
                      ) : null}
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <div className="sales-section-card sales-section-lead">
        <div className="sales-section-head">
          <h2>Availability</h2>
          <p>
            Manage your regular weekly hours and block specific unavailable dates. Weekly
            hours are based on <strong>{profileForm.timezone}</strong>.
          </p>
        </div>

        <div className="bookme-availability-layout">
          <div className="bookme-availability-panel">
            <div className="bookme-panel-head">
              <div>
                <h3>Weekly Hours</h3>
                <p>Choose the days and time ranges visitors can book.</p>
              </div>
            </div>

            <div className="bookme-hours-table">
              <div className="bookme-hours-header">
                <span>Day</span>
                <span>Available Times</span>
              </div>

              {availability.map((dayAvailability, dayIndex) => {
                const day = DAYS.find((item) => item.value === dayAvailability.dayOfWeek);
                const firstRange = dayAvailability.blocks[0];
                const secondRange = dayAvailability.blocks[1];

                return (
                  <div key={dayAvailability.dayOfWeek} className="bookme-hours-row">
                    <div className="bookme-hours-day">
                      <label className="bookme-checkbox-label">
                        <input
                          type="checkbox"
                          checked={dayAvailability.enabled}
                          onChange={(e) =>
                            updateAvailabilityDay(dayIndex, {
                              enabled: e.target.checked
                            })
                          }
                        />
                        {day?.label}
                      </label>
                    </div>

                    <div className="bookme-hours-ranges">
                      {dayAvailability.enabled ? (
                        <>
                          <div className="bookme-time-range">
                            <input
                              type="time"
                              value={firstRange.startTime}
                              onChange={(e) =>
                                updateAvailabilityBlock(dayIndex, 0, {
                                  startTime: e.target.value
                                })
                              }
                            />

                            <span>to</span>

                            <input
                              type="time"
                              value={firstRange.endTime}
                              onChange={(e) =>
                                updateAvailabilityBlock(dayIndex, 0, {
                                  endTime: e.target.value
                                })
                              }
                            />
                          </div>

                          {secondRange.enabled ? (
                            <div className="bookme-time-range">
                              <input
                                type="time"
                                value={secondRange.startTime}
                                onChange={(e) =>
                                  updateAvailabilityBlock(dayIndex, 1, {
                                    startTime: e.target.value
                                  })
                                }
                              />

                              <span>to</span>

                              <input
                                type="time"
                                value={secondRange.endTime}
                                onChange={(e) =>
                                  updateAvailabilityBlock(dayIndex, 1, {
                                    endTime: e.target.value
                                  })
                                }
                              />

                              <button
                                type="button"
                                className="bookme-link-button"
                                onClick={() =>
                                  updateAvailabilityBlock(dayIndex, 1, {
                                    enabled: false
                                  })
                                }
                              >
                                Remove
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="bookme-add-range-btn"
                              onClick={() =>
                                updateAvailabilityBlock(dayIndex, 1, {
                                  enabled: true
                                })
                              }
                            >
                              + Add another time range
                            </button>
                          )}
                        </>
                      ) : (
                        <span className="bookme-unavailable-label">Unavailable</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="bookme-actions">
              <button
                className="sales-primary-btn"
                onClick={handleSaveAvailability}
                disabled={savingAvailability}
              >
                {savingAvailability ? "Saving..." : "Save Weekly Hours"}
              </button>
            </div>
          </div>

          <div className="bookme-availability-panel">
            <div className="bookme-panel-head">
              <div>
                <h3>Unavailable Dates</h3>
                <p>Block vacation days, holidays, or personal days within the next 30 days.</p>
              </div>
            </div>

            <div className="bookme-date-save-card">
              <Field label="Date">
                <input
                  type="date"
                  value={newUnavailableDate}
                  min={unavailableDateRange.startDate}
                  max={unavailableDateRange.endDate}
                  onChange={(e) => setNewUnavailableDate(e.target.value)}
                />
              </Field>

              <Field label="Reason">
                <input
                  value={newUnavailableReason}
                  onChange={(e) => setNewUnavailableReason(e.target.value)}
                  placeholder="Optional, e.g. Vacation"
                />
              </Field>

              <button
                className="sales-primary-btn"
                onClick={handleSaveUnavailableDates}
                disabled={savingUnavailableDates}
              >
                {savingUnavailableDates ? "Saving..." : "Save Date"}
              </button>
            </div>

            <div className="bookme-unavailable-list">
              {unavailableDates.length === 0 ? (
                <p className="settings-muted-text">No unavailable dates saved.</p>
              ) : (
                unavailableDates.map((item) => (
                  <div key={item.unavailableDate} className="bookme-unavailable-row">
                    <div>
                      <strong>{formatDisplayDate(item.unavailableDate)}</strong>
                      <p>{item.reason || "Unavailable"}</p>
                    </div>

                    <button
                      type="button"
                      className="sales-secondary-btn"
                      onClick={() => handleRemoveUnavailableDate(item.unavailableDate)}
                    >
                      Remove
                    </button>
                  </div>
                ))
              )}
            </div>

            {unavailableDates.length > 0 ? (
              <div className="bookme-actions">
                <button
                  className="sales-secondary-btn"
                  onClick={handleSaveUnavailableDates}
                  disabled={savingUnavailableDates}
                >
                  {savingUnavailableDates ? "Saving..." : "Save Removed Dates"}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="sales-section-card sales-section-subscription">
        <div className="sales-section-head">
          <h2>Upcoming BookMe Appointments</h2>
          <p>Preview your scheduled BookMe appointments.</p>
        </div>

        <div className="bookme-appointments-table-wrap">
          <table className="bookme-appointments-table">
            <thead>
              <tr>
                <th>Attendee</th>
                <th>Email</th>
                <th>Start</th>
                <th>Status</th>
                <th>Match</th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              {appointments.length === 0 ? (
                <tr>
                  <td colSpan="6">No BookMe appointments found.</td>
                </tr>
              ) : (
                appointments.map((appointment) => (
                  <tr key={appointment.id}>
                    <td>{appointment.attendee_name}</td>
                    <td>{appointment.attendee_email}</td>
                    <td>{appointment.start_at}</td>
                    <td>{appointment.status}</td>
                    <td>
                      {appointment.client_id
                        ? "Client"
                        : appointment.lead_id
                          ? "Lead"
                          : "External"}
                    </td>
                    <td>
                      {appointment.status !== "cancelled" ? (
                        <button
                          type="button"
                          className="sales-secondary-btn"
                          onClick={() => handleCancelAppointment(appointment.id)}
                        >
                          Cancel
                        </button>
                      ) : (
                        <span className="settings-muted-text">Cancelled</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BookMeSettingsPanel;